import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import '../Styles/VendorDashboard.css';

// ── API endpoints ─────────────────────────────────────────────────────────────
const VIM_DASHBOARD_API = 'https://ocrtool.savictech.com/api/vim/dashboard';
const VIM_JOURNEY_API   = 'https://ocrtool.savictech.com/api/vim/journey/po';
const VIM_INVOICE_API   = 'https://ocrtool.savictech.com/api/vim/invoices';

// ── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  PO_CREATED:     { label: 'PO Created',      color: '#718096', bg: '#f7fafc', border: '#e2e8f0' },
  GR_PARTIAL:     { label: 'GR Partial',       color: '#b7791f', bg: '#fffff0', border: '#fbd38d' },
  GR_COMPLETED:   { label: 'GR Completed',     color: '#2b6cb0', bg: '#ebf8ff', border: '#bee3f8' },
  INVOICE_POSTED: { label: 'Invoice Posted',   color: '#553c9a', bg: '#faf5ff', border: '#d6bcfa' },
  BLOCKED:        { label: 'Blocked',          color: '#c53030', bg: '#fff5f5', border: '#fc8181' },
  APPROVED:       { label: 'Approved',         color: '#276749', bg: '#f0fff4', border: '#9ae6b4' },
  PAID:           { label: 'Paid',             color: '#22543d', bg: '#c6f6d5', border: '#68d391' },
  CANCELLED:      { label: 'Cancelled',        color: '#4a5568', bg: '#edf2f7', border: '#cbd5e0' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (val, currency) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: currency || 'INR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(val || 0);

const fmtDate = (str) => {
  if (!str) return '—';
  // already formatted like 2024-03-10, just return as-is or pretty-print
  try { return new Date(str).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return str; }
};

// ── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#4a5568', bg: '#edf2f7', border: '#cbd5e0' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
    }}>
      {cfg.label}
    </span>
  );
};

// ── Horizontal Timeline ──────────────────────────────────────────────────────
const Timeline = ({ timeline }) => {
  if (!timeline?.length) return null;
  return (
    <div className="vim-timeline">
      {timeline.map((step, i) => {
        const isDone    = step.completed && !step.active;
        const isCurrent = step.completed && step.active;
        return (
          <React.Fragment key={i}>
            <div className={`vim-step ${isDone ? 'step-done' : isCurrent ? 'step-current' : 'step-future'}`}>
              <div className="vim-step-circle">
                {isDone ? '✓' : isCurrent ? '●' : '○'}
              </div>
              <div className="vim-step-label">{step.label}</div>
              {step.timestamp && <div className="vim-step-date">{fmtDate(step.timestamp)}</div>}
            </div>
            {i < timeline.length - 1 && (
              <div className={`vim-connector ${step.completed ? 'connector-done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── InfoRow ──────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 100 }}>
    <span style={{ fontSize: 10, color: '#718096', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: color || '#1a202c', marginTop: 2 }}>{value ?? '—'}</span>
  </div>
);

// ── SectionHeading ───────────────────────────────────────────────────────────
const SectionHeading = ({ children }) => (
  <h4 style={{ margin: '20px 0 10px', fontSize: 13, fontWeight: 600, color: '#2d3748', borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>{children}</h4>
);

// ── GRTable (shared) ─────────────────────────────────────────────────────────
const GRTable = ({ rows }) => (
  <div className="modal-table-container">
    <table className="po-table">
      <thead>
        <tr>
          <th>GR Document</th><th>GR Date</th><th>Material</th>
          <th>Plant</th><th style={{ textAlign: 'right' }}>Quantity</th>
          <th style={{ textAlign: 'right' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((gr, i) => (
          <tr key={i}>
            <td>{gr.material_doc}</td><td>{fmtDate(gr.gr_date)}</td>
            <td>{gr.material}</td><td>{gr.plant}</td>
            <td style={{ textAlign: 'right' }}>{gr.quantity}</td>
            <td style={{ textAlign: 'right' }}>{formatCurrency(gr.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────
const VendorDashboard = () => {
  const vendorName = localStorage.getItem('vendorName') || '';

  const [vendorCode, setVendorCode]       = useState(localStorage.getItem('vendorCode') || '600000');
  const [inputCode, setInputCode]         = useState(localStorage.getItem('vendorCode') || '600000');

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [fromDate, setFromDate]           = useState('');
  const [toDate, setToDate]               = useState('');
  const [activeTab, setActiveTab]         = useState('invoices'); // 'invoices' | 'pos'

  // PO Journey modal
  const [poJourney, setPoJourney]       = useState(null);
  const [loadingPO, setLoadingPO]       = useState(false);
  const [showPOModal, setShowPOModal]   = useState(false);

  // Invoice Journey modal
  const [invJourney, setInvJourney]         = useState(null);
  const [loadingInv, setLoadingInv]         = useState(false);
  const [showInvModal, setShowInvModal]     = useState(false);

  // ── Fetch dashboard ────────────────────────────────────────────────────────
  const fetchDashboard = useCallback(async (forceRefresh = false) => {
    if (!vendorCode) {
      setError('Please enter a vendor code.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      let url = `${VIM_DASHBOARD_API}?vendor_code=${vendorCode}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate)   url += `&to_date=${toDate}`;
      if (forceRefresh) url += `&force_refresh=true`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'API returned failure');
      setDashboardData(data);
    } catch (err) {
      setError(err.message || 'Unable to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [vendorCode, fromDate, toDate]);

  // Fetch whenever vendorCode or dates change (vendorCode changes on Search click)
  useEffect(() => { if (vendorCode) fetchDashboard(); }, [vendorCode]); // eslint-disable-line

  // ── Fetch PO Journey ───────────────────────────────────────────────────────
  const fetchPOJourney = async (poNumber) => {
    setPoJourney(null);
    setShowPOModal(true);
    setLoadingPO(true);
    try {
      const res = await fetch(`${VIM_JOURNEY_API}/${poNumber}?vendor_code=${vendorCode}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'API returned failure');
      setPoJourney(data);
    } catch (err) {
      setPoJourney({ _error: err.message });
    } finally {
      setLoadingPO(false);
    }
  };

  // ── Fetch Invoice Journey ──────────────────────────────────────────────────
  const fetchInvJourney = async (invoiceDoc, fiscalYear) => {
    setInvJourney(null);
    setShowInvModal(true);
    setLoadingInv(true);
    try {
      const res = await fetch(`${VIM_INVOICE_API}/${invoiceDoc}/${fiscalYear}?vendor_code=${vendorCode}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'API returned failure');
      setInvJourney(data);
    } catch (err) {
      setInvJourney({ _error: err.message });
    } finally {
      setLoadingInv(false);
    }
  };

  // ── Jump from PO modal → Invoice modal ────────────────────────────────────
  const openInvFromPO = (inv) => {
    setShowPOModal(false);
    fetchInvJourney(inv.invoice_doc, inv.fiscal_year);
  };

  // ── Initial full-page loading state (first fetch only) ────────────────────
  if (loading && !dashboardData && vendorCode) {
    return (
      <div className="vendor-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading invoice dashboard…</p>
        </div>
      </div>
    );
  }

  const summary = dashboardData?.summary;

  return (
    <div className="vendor-dashboard">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="logo-section">
            <span className="vendor-login-text">Vendor Portal</span>
          </div>
        </div>
        <div className="header-center">
          <h1 className="dashboard-main-title">Invoice Lifecycle Dashboard</h1>
          <p className="dashboard-subtitle">Track your purchase orders &amp; invoices in real time</p>
        </div>
        <div className="header-right">
          {vendorName && (
            <div className="vendor-info">
              <span className="info-label">Vendor</span>
              <span className="info-value" style={{ fontWeight: 800, color: '#0f4c81' }}>{vendorName}</span>
            </div>
          )}
          <div className="vendor-info">
            <span className="info-label">Vendor Code</span>
            <span className="info-value">{vendorCode || '—'}</span>
          </div>
          {dashboardData?.generated_at && (
            <span className="last-updated-btn">
              Updated {new Date(dashboardData.generated_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────── */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Vendor Code</label>
          <input
            type="text"
            value={inputCode}
            onChange={e => setInputCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setVendorCode(inputCode); } }}
            placeholder="e.g. 600000"
          />
        </div>
        {/* <div className="filter-group">
          <label>From Date</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>To Date</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
        </div> */}
        <button className="search-btn" onClick={() => setVendorCode(inputCode)} disabled={loading}>
          {loading ? 'Loading…' : 'Search'}
        </button>
        <button className="search-btn" style={{ background: '#718096' }} onClick={() => fetchDashboard(true)} disabled={loading || !vendorCode}>
          Refresh
        </button>
      </div>

      {/* Loading overlay while re-fetching */}
      {loading && dashboardData && (
        <div className="loading-overlay"><div className="spinner"></div></div>
      )}

      {/* Error banner */}
      {error && (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={() => fetchDashboard()}>Retry</button>
        </div>
      )}

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      {summary && (
        <div className="kpi-cards-grid" style={{ paddingTop: 15, gridTemplateColumns: 'repeat(6, 1fr)' }}>
          <div className="kpi-card-new">
            <div className="kpi-content">
              <div className="kpi-title-new">Total POs</div>
              <div className="kpi-value-new">{summary.total_pos}</div>
            </div>
          </div>
          <div className="kpi-card-new">
            <div className="kpi-content">
              <div className="kpi-title-new">Total Invoices</div>
              <div className="kpi-value-new">{summary.total_invoices}</div>
            </div>
          </div>
          <div className="kpi-card-new" style={{ borderColor: summary.blocked_count > 0 ? '#fc8181' : undefined }}>
            <div className="kpi-content">
              <div className="kpi-title-new">Blocked</div>
              <div className="kpi-value-new" style={{ color: summary.blocked_count > 0 ? '#c53030' : '#1a202c' }}>
                {summary.blocked_count}
              </div>
            </div>
          </div>
          <div className="kpi-card-new">
            <div className="kpi-content">
              <div className="kpi-title-new">Paid</div>
              <div className="kpi-value-new" style={{ color: '#22543d' }}>{summary.paid_count}</div>
            </div>
          </div>
          <div className="kpi-card-new">
            <div className="kpi-content">
              <div className="kpi-title-new">Pending</div>
              <div className="kpi-value-new" style={{ color: '#b7791f' }}>{summary.pending_count}</div>
            </div>
          </div>
          <div className="kpi-card-new" style={{ borderColor: summary.overdue_count > 0 ? '#fc8181' : undefined }}>
            <div className="kpi-content">
              <div className="kpi-title-new">Overdue</div>
              <div className="kpi-value-new" style={{ color: summary.overdue_count > 0 ? '#e53e3e' : '#1a202c' }}>
                {summary.overdue_count}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      {dashboardData && (
        <div className="vim-tabs">
          <button
            className={`vim-tab-btn${activeTab === 'invoices' ? ' active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            Invoices ({dashboardData.invoices?.length || 0})
          </button>
          <button
            className={`vim-tab-btn${activeTab === 'pos' ? ' active' : ''}`}
            onClick={() => setActiveTab('pos')}
          >
            Purchase Orders ({dashboardData.purchase_orders?.length || 0})
          </button>
        </div>
      )}

      {/* ── Invoices Table ─────────────────────────────────────────────── */}
      {dashboardData && activeTab === 'invoices' && (
        <div className="table-section">
          <div className="table-container">
            <table className="supplier-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Invoice Doc</th>
                  <th>PO Number</th>
                  <th>Invoice Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Aging</th>
                  <th style={{ textAlign: 'left' }}>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.invoices?.length ? dashboardData.invoices.map((inv, i) => (
                  <tr key={i} onClick={() => fetchInvJourney(inv.invoice_doc, inv.fiscal_year)} style={{ cursor: 'pointer' }}>
                    <td style={{ textAlign: 'left' }}>
                      <span style={{ color: '#4318FF', fontWeight: 700 }}>{inv.invoice_doc}</span>
                      {inv.external_invoice_ref && (
                        <div style={{ fontSize: 10, color: '#718096', fontWeight: 400, marginTop: 2 }}>{inv.external_invoice_ref}</div>
                      )}
                    </td>
                    <td>{inv.po_number}</td>
                    <td>{fmtDate(inv.invoice_date)}</td>
                    <td>{formatCurrency(inv.gross_amount, inv.currency)}</td>
                    <td><StatusBadge status={inv.vim_status} /></td>
                    <td style={{ color: (inv.aging_days || 0) > 30 ? '#e53e3e' : '#4a5568' }}>
                      {inv.aging_days != null ? `${inv.aging_days}d` : '—'}
                    </td>
                    <td style={{ textAlign: 'left', fontSize: 11, color: '#4a5568', maxWidth: 220, whiteSpace: 'normal' }}>
                      {inv.next_action}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#718096' }}>No invoices found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Purchase Orders Table ──────────────────────────────────────── */}
      {dashboardData && activeTab === 'pos' && (
        <div className="table-section">
          <div className="table-container">
            <table className="supplier-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>PO Number</th>
                  <th>PO Date</th>
                  <th>Items</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'left' }}>Next Action</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.purchase_orders?.length ? dashboardData.purchase_orders.map((po, i) => (
                  <tr key={i} onClick={() => fetchPOJourney(po.po_number)} style={{ cursor: 'pointer' }}>
                    <td style={{ textAlign: 'left', color: '#4318FF', fontWeight: 700 }}>{po.po_number}</td>
                    <td>{fmtDate(po.po_date)}</td>
                    <td>{po.item_count}</td>
                    <td>{formatCurrency(po.total_amount, po.currency)}</td>
                    <td><StatusBadge status={po.vim_status} /></td>
                    <td style={{ textAlign: 'left', fontSize: 11, color: '#4a5568', maxWidth: 220, whiteSpace: 'normal' }}>
                      {po.next_action}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#718096' }}>No purchase orders found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PO Journey Modal ───────────────────────────────────────────── */}
      {showPOModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowPOModal(false)}>
          <div className="modal-content" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>PO Journey — {poJourney?.po_number || '…'}</h2>
              <button className="modal-close" onClick={() => setShowPOModal(false)}>×</button>
            </div>

            {loadingPO ? (
              <div className="modal-loading"><div className="spinner"></div><p>Loading…</p></div>
            ) : poJourney?._error ? (
              <div className="modal-body"><p className="error-message">{poJourney._error}</p></div>
            ) : poJourney && (
              <div className="modal-body">
                {/* Summary strip */}
                <div className="vim-info-strip">
                  <InfoRow label="Status" value={<StatusBadge status={poJourney.vim_status} />} />
                  <InfoRow label="Total Amount" value={formatCurrency(poJourney.total_amount, poJourney.currency)} />
                  <InfoRow label="Company" value={poJourney.company_code} />
                  <InfoRow label="PO Date" value={fmtDate(poJourney.po_date)} />
                  {poJourney.block_reason && (
                    <InfoRow label="Block Reason" value={poJourney.block_reason} color="#c53030" />
                  )}
                </div>

                {poJourney.next_action && (
                  <div className="vim-next-action">{poJourney.next_action}</div>
                )}

                <SectionHeading>Process Timeline</SectionHeading>
                <Timeline timeline={poJourney.timeline} />

                {/* Line Items */}
                {poJourney.line_items?.length > 0 && (
                  <>
                    <SectionHeading>Line Items</SectionHeading>
                    <div className="modal-table-container">
                      <table className="po-table">
                        <thead>
                          <tr>
                            <th>Item</th><th>Material</th><th>Description</th>
                            <th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th>
                            <th style={{ textAlign: 'right' }}>Amount</th><th>Delivery Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poJourney.line_items.map((item, i) => (
                            <tr key={i}>
                              <td>{item.item_number}</td>
                              <td>{item.material}</td>
                              <td>{item.material_description || '—'}</td>
                              <td style={{ textAlign: 'right' }}>{item.order_quantity}</td>
                              <td>{item.unit}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(item.net_amount, item.currency)}</td>
                              <td>{fmtDate(item.delivery_date)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* GR History */}
                {poJourney.gr_history?.length > 0 && (
                  <>
                    <SectionHeading>Goods Receipt History</SectionHeading>
                    <GRTable rows={poJourney.gr_history} />
                  </>
                )}

                {/* Linked Invoices */}
                {poJourney.linked_invoices?.length > 0 && (
                  <>
                    <SectionHeading>Linked Invoices — click a row to view details</SectionHeading>
                    <div className="modal-table-container">
                      <table className="po-table">
                        <thead>
                          <tr>
                            <th>Invoice Doc</th><th>Invoice Date</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th>Status</th><th>Aging</th>
                          </tr>
                        </thead>
                        <tbody>
                          {poJourney.linked_invoices.map((inv, i) => (
                            <tr key={i} onClick={() => openInvFromPO(inv)} style={{ cursor: 'pointer' }}>
                              <td style={{ color: '#4318FF', fontWeight: 600 }}>{inv.invoice_doc}</td>
                              <td>{fmtDate(inv.invoice_date)}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(inv.gross_amount, inv.currency)}</td>
                              <td><StatusBadge status={inv.vim_status} /></td>
                              <td>{inv.aging_days != null ? `${inv.aging_days}d` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="modal-footer">
              <p>PO {poJourney?.po_number} · {poJourney?.company_code}</p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Invoice Journey Modal ──────────────────────────────────────── */}
      {showInvModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowInvModal(false)}>
          <div className="modal-content" style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invoice — {invJourney?.invoice_doc || '…'}</h2>
              <button className="modal-close" onClick={() => setShowInvModal(false)}>×</button>
            </div>

            {loadingInv ? (
              <div className="modal-loading"><div className="spinner"></div><p>Loading…</p></div>
            ) : invJourney?._error ? (
              <div className="modal-body"><p className="error-message">{invJourney._error}</p></div>
            ) : invJourney && (
              <div className="modal-body">
                {/* Summary strip */}
                <div className="vim-info-strip">
                  <InfoRow label="Status" value={<StatusBadge status={invJourney.vim_status} />} />
                  <InfoRow label="Gross Amount" value={formatCurrency(invJourney.gross_amount, invJourney.currency)} />
                  {invJourney.tax_amount != null && (
                    <InfoRow label="Tax" value={formatCurrency(invJourney.tax_amount, invJourney.currency)} />
                  )}
                  <InfoRow label="Payment Terms" value={invJourney.payment_terms} />
                  <InfoRow label="Invoice Date" value={fmtDate(invJourney.invoice_date)} />
                  {invJourney.external_invoice_ref && (
                    <InfoRow label="Ref" value={invJourney.external_invoice_ref} />
                  )}
                  {invJourney.block_reason && (
                    <InfoRow label="Block Reason" value={invJourney.block_reason} color="#c53030" />
                  )}
                </div>

                {invJourney.next_action && (
                  <div className="vim-next-action">{invJourney.next_action}</div>
                )}

                {/* Payment info banner */}
                {invJourney.payment_info?.available && (
                  <div className="vim-payment-banner">
                    <div className="vim-payment-label">Payment Received</div>
                    <div className="vim-payment-details">
                      <span><strong>Doc:</strong> {invJourney.payment_info.payment_doc}</span>
                      <span><strong>Date:</strong> {fmtDate(invJourney.payment_info.payment_date)}</span>
                      <span><strong>Amount:</strong> {formatCurrency(invJourney.payment_info.payment_amount, invJourney.payment_info.currency)}</span>
                    </div>
                  </div>
                )}

                <SectionHeading>Process Timeline</SectionHeading>
                <Timeline timeline={invJourney.timeline} />

                {/* Line Items */}
                {invJourney.line_items?.length > 0 && (
                  <>
                    <SectionHeading>Line Items</SectionHeading>
                    <div className="modal-table-container">
                      <table className="po-table">
                        <thead>
                          <tr>
                            <th>Item</th><th>PO Number</th><th>Material</th>
                            <th style={{ textAlign: 'right' }}>Qty</th><th>Unit</th>
                            <th style={{ textAlign: 'right' }}>Amount</th><th>Tax Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invJourney.line_items.map((item, i) => (
                            <tr key={i}>
                              <td>{item.item_number}</td>
                              <td>{item.po_number}</td>
                              <td>{item.material}</td>
                              <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                              <td>{item.unit}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(item.net_amount, item.currency)}</td>
                              <td>{item.tax_code || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* GR History */}
                {invJourney.gr_history?.length > 0 && (
                  <>
                    <SectionHeading>Goods Receipt History</SectionHeading>
                    <GRTable rows={invJourney.gr_history} />
                  </>
                )}
              </div>
            )}

            <div className="modal-footer">
              <p>Invoice {invJourney?.invoice_doc} · FY {invJourney?.fiscal_year}</p>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default VendorDashboard;
